import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Deal } from "@/constants/offers";

const MARKETPLACE_LABELS: Record<string, string> = {
  amazon: "Amazon",
  magalu: "Magalu",
  "mercado-livre": "Mercado Livre",
  outros: "Outros",
};

function formatBRL(value: number) {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

const DealCard: React.FC<{ deal: Deal }> = ({ deal }) => {
  const [imgSrc, setImgSrc] = useState<string>(deal.image);

  useEffect(() => {
    let cancelled = false;
    async function resolveImage() {
      // Only try to resolve if using placeholder and we have a productUrl
      if (!deal.productUrl) return;
      if (!imgSrc || imgSrc === "/placeholder.svg") {
        try {
          const q = encodeURIComponent(deal.productUrl);
          const resp = await fetch(`/api/fetch-og-image?url=${q}`);
          if (!resp.ok) return; // gracefully keep placeholder in local dev
          const data = await resp.json();
          if (!cancelled && data?.image) {
            setImgSrc(data.image);
          }
        } catch {
          // ignore errors (likely local dev without serverless runtime)
        }
      }
    }
    resolveImage();
    return () => {
      cancelled = true;
    };
  }, [deal.productUrl, imgSrc]);

  return (
    <Card className="overflow-hidden border-border hover:border-brand-roxo/30 hover:shadow-lg transition-all rounded-2xl">
      <div className="aspect-[4/3] bg-muted">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imgSrc}
          alt={deal.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            const t = e.target as HTMLImageElement;
            t.src = "/placeholder.svg";
            setImgSrc("/placeholder.svg");
          }}
        />
      </div>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-brand-azul leading-tight">
            {deal.title}
          </h3>
          <Badge variant="outline" className="border-brand-azul/20 text-brand-azul/70 shrink-0">
            {MARKETPLACE_LABELS[deal.marketplace] || deal.marketplace}
          </Badge>
        </div>
        {typeof deal.price === "number" ? (
          <div className="flex items-baseline gap-2">
            <div className="font-display text-2xl font-extrabold text-brand-azul">{formatBRL(deal.price)}</div>
            {deal.bestPrice && deal.price != null && deal.bestPrice < deal.price ? (
              <div className="text-xs text-brand-azul/50">
                Melhor histórico {formatBRL(deal.bestPrice)}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="text-sm text-brand-azul/60">Verifique o preço na Amazon</div>
        )}
        <div className="text-xs text-brand-azul/50">
          Descoberto em {new Date(deal.discoveredAt).toLocaleDateString("pt-BR")}
        </div>
        {deal.tags && deal.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {deal.tags.map((t) => (
              <Badge key={t} variant="secondary" className="text-xs">
                {t}
              </Badge>
            ))}
          </div>
        ) : null}
        <div className="pt-2">
          <Button asChild className="bg-brand-roxo text-white hover:bg-brand-roxo/90 w-full rounded-full font-semibold">
            <a href={deal.affiliateUrl} target="_blank" rel="noopener noreferrer">
              Ver na loja
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DealCard;
